import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  useCalendarConnectionQuery,
  useDisconnectCalendarMutation,
  useStartCalendarAuthorizationMutation,
} from '@/hooks/useCalendarSync';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

export function CalendarSyncSection() {
  const { t } = useTranslation('settings');
  const connectionQuery = useCalendarConnectionQuery();
  const startAuthorization = useStartCalendarAuthorizationMutation();
  const disconnectCalendar = useDisconnectCalendarMutation();
  const [isConfirmingDisconnect, setIsConfirmingDisconnect] = useState(false);

  const connection = connectionQuery.data;
  const isConnected = connection !== undefined && connection.isConnected === true;

  function connect() {
    startAuthorization.mutate(undefined, {
      onSuccess: function redirectToGoogle(authorizationUrl) {
        window.location.assign(authorizationUrl);
      },
      onError: function reportError(error) {
        const message = resolveApiErrorMessage(error);
        showErrorNotification(message.length > 0 ? message : t('calendarSync.connectError'));
      },
    });
  }

  function disconnect() {
    disconnectCalendar.mutate(undefined, {
      onSuccess: function reportDisconnected() {
        setIsConfirmingDisconnect(false);
        showSuccessNotification(t('calendarSync.disconnected'));
      },
      onError: function reportError(error) {
        setIsConfirmingDisconnect(false);
        const message = resolveApiErrorMessage(error);
        showErrorNotification(message.length > 0 ? message : t('calendarSync.disconnectError'));
      },
    });
  }

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="RefreshCw" />
        </span>
        <h2 className="text-sm font-bold text-zooindigo">{t('calendarSync.title')}</h2>
      </div>
      <p className="text-sm text-zoomuted mb-5">{t('calendarSync.subtitle')}</p>

      {connectionQuery.isLoading === true ? (
        <div className="py-8 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : isConnected === true && connection !== undefined ? (
        <div className="border border-zooborder rounded-xl p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-bold text-zooindigo truncate">
                  {connection.googleAccountEmail === null
                    ? t('calendarSync.connectedTitle')
                    : connection.googleAccountEmail}
                </span>
                <Badge tone="success" size="sm" uppercase>
                  {t('calendarSync.connectedBadge')}
                </Badge>
              </div>
              <p className="text-[11px] text-zoomuted mt-1">
                {connection.lastSyncedAt === null
                  ? t('calendarSync.neverSynced')
                  : t('calendarSync.lastSynced', {
                      when: formatIsoDateTimeToTimestamp(connection.lastSyncedAt),
                    })}
              </p>
            </div>
            <Button
              variant="outline"
              size="chip"
              className="shrink-0"
              onClick={function openConfirm() {
                setIsConfirmingDisconnect(true);
              }}
            >
              <Icon name="Unplug" />
              {t('calendarSync.disconnect')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="border border-zooborder rounded-xl p-4">
          <p className="text-sm text-zooindigo">{t('calendarSync.description')}</p>
          <p className="text-[12px] text-zoomuted mt-2">{t('calendarSync.dataNote')}</p>
          <Button
            className="mt-4"
            isLoading={startAuthorization.isPending}
            onClick={connect}
          >
            {startAuthorization.isPending ? null : <Icon name="Calendar" />}
            {startAuthorization.isPending
              ? t('calendarSync.connecting')
              : t('calendarSync.connect')}
          </Button>
        </div>
      )}

      {isConfirmingDisconnect === true ? (
        <ConfirmDialog
          tone="danger"
          iconName="Unplug"
          title={t('calendarSync.disconnectConfirmTitle')}
          description={t('calendarSync.disconnectConfirmDescription')}
          confirmLabel={t('calendarSync.disconnectConfirmAction')}
          isConfirming={disconnectCalendar.isPending}
          onConfirm={disconnect}
          onCancel={function closeConfirm() {
            setIsConfirmingDisconnect(false);
          }}
        />
      ) : null}
    </Card>
  );
}
